/**
 * Pure helpers for reading prices off a CdvPurchase (cordova-plugin-purchase
 * v13) product. No imports, so `node --test tests/iapPricing.test.mjs` can
 * exercise them against the exact offer shapes the Apple adapter builds.
 *
 * Guideline 3.1.2(c) — the BILLED amount must be the most conspicuous price
 * on the paywall. NEVER read it from `product.pricing.price`: that getter is
 * `offers[0].pricingPhases[0]`, and the Apple adapter puts the introductory
 * phase FIRST for any account eligible for the intro offer (every App Review
 * sandbox account is). On such an account `pricing.price` is the £0.00 free
 * trial, so PowderLog 1.2.0 build 11 rendered "£0.00" as its largest pricing
 * element and "Subscribe for £0.00/year" on the CTA — rejected 2026-09-08.
 * Canonical: shared-docs/in-app-purchases.md → Paywall Layout.
 */

// Parse an ISO-8601 billing period (P3D, P1W, P1M...) to days; null if unknown.
export function iso8601PeriodToDays(period) {
  const m = /^P(\d+)([DWMY])$/.exec(period || '');
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return { D: n, W: n * 7, M: n * 30, Y: n * 365 }[m[2]] ?? null;
}

// The offer a plain `store.order(product.getOffer())` purchases: the adapter's
// Default offer when present, otherwise the first (Apple only emits an
// 'Introductory' offer in its place when the intro offer arrives via
// `discounts`).
function primaryOffer(iapProduct) {
  const offers = iapProduct?.offers || [];
  return offers.find((o) => o?.offerType === 'Default') || offers[0] || null;
}

// The recurring pricing phase — the amount charged every period once any
// trial / intro phase has run out. Returns { price, currency } or null when
// the product has no priced phase (caller falls back to its static default).
export function getBilledPricing(iapProduct) {
  const phases = primaryOffer(iapProduct)?.pricingPhases || [];
  if (!phases.length) return null;
  const recurring = phases.find((p) => p?.recurrenceMode === 'INFINITE_RECURRING');
  // Every adapter appends the base recurring price as the LAST phase; the
  // recurrenceMode match is the explicit form, the last phase the safety net.
  const phase = recurring || phases[phases.length - 1];
  if (!phase?.price) return null;
  return { price: phase.price, currency: phase.currency || null };
}

// Detect a StoreKit introductory free trial on a product. CdvPurchase v13
// surfaces intro offers as pricing phases with paymentMode FREE_TRIAL (the
// enum value is 'FreeTrial'). Returns { periodDays } or null.
export function getIntroTrial(iapProduct) {
  for (const offer of iapProduct?.offers || []) {
    for (const phase of offer?.pricingPhases || []) {
      const mode = String(phase?.paymentMode || '');
      if (/free.?trial/i.test(mode)) {
        return { periodDays: iso8601PeriodToDays(phase.billingPeriod) };
      }
    }
  }
  return null;
}
